import { useMutation, useQuery } from '@apollo/client';
import React, { Fragment, useEffect, useMemo, useState, useCallback, useRef } from 'react';

import styled, { ThemeProvider } from 'styled-components';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { getSurgeryInstructionsTemplates, updateProcedureSurgeryInstructions } from '../../../../graph/procedures';
import LinkButton from '@/se/components/LinkButton';
import SelectInput from '@/se/components/inputs/SelectInput';
import Button from '@/se/components/Button[DEPRECATED]';
import { TruncatedText } from '@/se/components/typography';
import { format } from 'date-fns';
import RichTextPage from './RichTextPage';
import { InlineColumn, ResendActionsInstructions } from '../columns';
import InstructionsAccepted from './InstructionsAccepted';
import { light as lightTheme } from '../../../../theme';
import { TemplateContext, TemplateStateProvider } from './TemplateContext';
import { generateInstructionsPdf } from '../pdf/InstructionPdfGenerator';
import { ZonedDateTime } from '@js-joda/core';
import { getNotificationTemplate } from '../../../../graph/notificationTemplates';
import { eventTypes } from '../ProcedureEventLog';
import { filter } from 'lodash';
import isNil from 'lodash/fp/isNil';
import { Print } from '@material-ui/icons';
import { withScope } from '../../../../contexts/ScopeContext';
import { useScope } from '../../../../hooks/useScope';
import { SendingSetting } from '../../notificationTemplate/NotificationTemplates';
import { getInstructionTemplateLabels } from '../../../../util/InstructionTemplatesService';

// Module-level cache to persist saved instructions across tab switches
// This is needed because Apollo's cache.modify doesn't update subscription data
// The cache is cleared on full page refresh, which is the desired behavior
const savedInstructionsCache = new Map<number, { content: string; timestamp: number }>();

// Clean up old entries (older than 5 minutes) to prevent memory leaks
const CACHE_TTL_MS = 5 * 60 * 1000;
const cleanupCache = () => {
  const now = Date.now();
  for (const [key, value] of savedInstructionsCache.entries()) {
    if (now - value.timestamp > CACHE_TTL_MS) {
      savedInstructionsCache.delete(key);
    }
  }
};

export const InstructionBody = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: center;

  > * + * {
    margin-left: 3em;
  }
`;

export const TemplateEditor = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 0 1 44.2em; // Do Beograda
  max-width: 100%;
`;

export const EditorActions = styled.div<{ flexEnd?: boolean }>`
  display: flex;
  justify-content: ${props => (props.flexEnd ? 'flex-end' : 'space-between')};
  padding-right: 10%;
  gap: 10px;
  margin-bottom: 1em;
`;

const EditorPreview = styled.div`
  background: ${props => props.theme.backgroundColor.string()};
  color: ${props => props.theme.textColor.string()};
  box-shadow: 2px 2px 7px 4px rgba(0, 0, 0, 0.15);
  padding: 2rem;
  font-size: 0.8em;
  min-height: 54rem;
`;

const TemplatePicker = ({ selected, onSelected, templates }: any) => {
  const scope = useScope();
  const hospitalId = scope?.hospital?.id;
  const correctedInstructionTemplateLabels = getInstructionTemplateLabels(hospitalId, templates);

  useEffect(() => {
    const options = Object.keys(correctedInstructionTemplateLabels);
    if (options.length > 0 && selected && options.indexOf(selected) < 0) {
      onSelected(options[0]);
    }
  }, [correctedInstructionTemplateLabels, onSelected, selected]);

  return (
    <SelectInput
      name="status"
      placeholder="Select"
      options={correctedInstructionTemplateLabels}
      value={selected}
      onChange={(value: any) => onSelected(value)}
      menuWidthFollowContent
      isSearchable={false}
      style={{
        width: 350,
        marginRight: '2rem',
      }}
    />
  );
};

const Actions = styled.div`
  display: flex;
  margin-bottom: 1em;
  > * + * {
    margin-left: 1em;
  }
`;

export const getInstructionLastSent = (data: any) => {
  const emailInstructionInvite = get(data, 'instructionsInviteSentAt');
  const smmInstructionInvites = filter(
    get(data, 'events'),
    ({ type }) => type === eventTypes.MessageDeliveryInstructions
  ).map(invite => invite?.timestamp);
  const lastTwoSentAt = [emailInstructionInvite, smmInstructionInvites?.[0]];
  // @ts-ignore
  return lastTwoSentAt.sort((a: any, b: any) => new Date(b) - new Date(a)).filter(lastSent => !isNil(lastSent));
};

export const SurgeryInstructions = withScope(({ data, scope, selectedTemplate, onSelectedTemplate }: any) => {
  const sortedLastTwoSentAt = getInstructionLastSent(data);
  const invite = sortedLastTwoSentAt?.[0];
  const procedureId = data?.id;

  // Clean up old cache entries periodically
  useEffect(() => {
    cleanupCache();
  }, []);

  // Get instruction data - prefer cached value if subscription hasn't caught up yet
  const subscriptionInstructionData = get(data, 'procedureInstructions', '{}');
  const cachedEntry = procedureId ? savedInstructionsCache.get(procedureId) : null;

  // Use cached content if it exists and is different from subscription
  // (meaning subscription hasn't caught up to our save yet)
  const instructionData = cachedEntry?.content || subscriptionInstructionData;

  // Clear cache entry if subscription has caught up (data matches cached)
  useEffect(() => {
    if (cachedEntry && subscriptionInstructionData === cachedEntry.content) {
      savedInstructionsCache.delete(procedureId);
    }
  }, [subscriptionInstructionData, cachedEntry, procedureId]);

  const defaults = {
    arrivalDate: format(get(data, 'startTime'), 'YYYY-MM-DD'),
  };
  const { formType: initialFormType, variables: variablesNoDef } = JSON.parse(instructionData) || {};
  // arrivalDate always comes from startTime (it's a derived, disabled field)
  // Other saved variables take precedence over defaults
  const initialVariables = { ...(variablesNoDef || {}), ...defaults };

  // Track what was last saved to compare for dirty state
  const [savedVariables, setSavedVariables] = useState(initialVariables);
  const [savedFormType, setSavedFormType] = useState(initialFormType);

  // Track the content we just saved to skip syncing from our own update
  const lastSavedContentRef = useRef<string | null>(null);

  // Sync saved state when instructionData changes (e.g., after refetch, subscription update, or cache hit)
  // BUT skip if this change is from our own save (to avoid overwriting local state)
  useEffect(() => {
    // Skip sync if this change is from our own save
    if (lastSavedContentRef.current === instructionData) {
      lastSavedContentRef.current = null;
      return;
    }
    setSavedVariables(initialVariables);
    setSavedFormType(initialFormType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionData]);

  const { data: surgeryTemplatesData } = useQuery(getSurgeryInstructionsTemplates);
  const templates = get(surgeryTemplatesData, 'surgeryInstructionsTemplates', []);
  const template = useMemo(
    () => templates.find((_: any) => _.type === selectedTemplate),
    [selectedTemplate, templates]
  );
  const templateContent = get(JSON.parse(get(template, 'content') || '{}') || {}, 'content');
  const currentFormType = get(template, 'type');

  const [mutate, { loading }] = useMutation(updateProcedureSurgeryInstructions);

  // handleSave is defined in the inner component which has access to TemplateContext state

  const { data: smsPatientReadyNotification } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'SurgeryInstructions', channel: 'SMS' },
  });
  const smsNotificationData = smsPatientReadyNotification?.getNotificationTemplate[0] || null;
  const { data: emailPatientReadyNotification } = useQuery(getNotificationTemplate, {
    variables: { trigger: 'SurgeryInstructions', channel: 'Email' },
  });
  const emailNotificationData = emailPatientReadyNotification?.getNotificationTemplate[0] || null;

  return (
    <TemplateStateProvider initialState={initialVariables}>
      <SurgeryInstructionsInner
        data={data}
        invite={invite}
        sortedLastTwoSentAt={sortedLastTwoSentAt}
        selectedTemplate={selectedTemplate}
        onSelectedTemplate={onSelectedTemplate}
        templates={templates}
        templateContent={templateContent}
        currentFormType={currentFormType}
        savedVariables={savedVariables}
        setSavedVariables={setSavedVariables}
        savedFormType={savedFormType}
        setSavedFormType={setSavedFormType}
        lastSavedContentRef={lastSavedContentRef}
        mutate={mutate}
        loading={loading}
        scope={scope}
        smsNotificationData={smsNotificationData}
        emailNotificationData={emailNotificationData}
      />
    </TemplateStateProvider>
  );
});

// Inner component that consumes TemplateContext with hooks
const SurgeryInstructionsInner = ({
  data,
  invite,
  sortedLastTwoSentAt,
  selectedTemplate,
  onSelectedTemplate,
  templates,
  templateContent,
  currentFormType,
  savedVariables,
  setSavedVariables,
  savedFormType,
  setSavedFormType,
  lastSavedContentRef,
  mutate,
  loading,
  scope,
  smsNotificationData,
  emailNotificationData,
}: any) => {
  const { variables: state, setVariables } = React.useContext(TemplateContext) as any;

  // Compute isDirty based on TemplateContext state
  const isDirty = !isEqual(savedVariables, state) || currentFormType !== savedFormType;

  const handleSave = useCallback(async () => {
    const newContent = JSON.stringify({ content: templateContent, variables: state, formType: currentFormType });
    // Track what we're about to save so the sync effect skips this update
    lastSavedContentRef.current = newContent;
    await mutate({
      variables: {
        procedureId: data.id,
        content: newContent,
      },
      update: cache => {
        // Directly update the cached procedureInstructions field
        // This works for both queries and subscriptions
        cache.modify({
          id: cache.identify({ __typename: 'Procedure', id: data.id }),
          fields: {
            procedureInstructions: () => newContent,
          },
        });
      },
    });
    // Save to module-level cache so it persists across tab switches
    // This is needed because Apollo's subscription data doesn't update from cache.modify
    savedInstructionsCache.set(data.id, { content: newContent, timestamp: Date.now() });
    // After successful save, update the saved state to match current TemplateContext state
    setSavedVariables(state);
    setSavedFormType(currentFormType);
  }, [
    mutate,
    data.id,
    templateContent,
    state,
    currentFormType,
    setSavedVariables,
    setSavedFormType,
    lastSavedContentRef,
  ]);

  const onPrint = useCallback(() => {
    generateInstructionsPdf({
      instructions: {
        content: templateContent,
        variables: state,
      },
      procedure: data,
      hospitalName: get(scope, 'hospital.name'),
      scheduleProvider: get(scope, 'hospital.scheduleProvider'),
    });
  }, [templateContent, state, data, scope]);

  const preSend = useCallback(async () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Do you want to save them and send?')) {
        await handleSave();
        return true;
      }
      return false;
    }
    return true;
  }, [isDirty, handleSave]);

  const handleCancel = useCallback(() => {
    setVariables(savedVariables);
  }, [savedVariables, setVariables]);

  return (
    <InstructionBody>
      <TemplateEditor>
        <EditorActions>
          <TemplatePicker selected={selectedTemplate} onSelected={onSelectedTemplate} templates={templates} />
          {/* @ts-ignore */}
          <Button onClick={onPrint}>
            <Print fontSize={'small'} style={{ paddingRight: '0.5rem' }} />
            Print Instructions
          </Button>
        </EditorActions>
        <ThemeProvider theme={lightTheme}>
          <Fragment>
            <Actions>
              {/* @ts-ignore */}
              <Button disabled={!isDirty || loading} onClick={handleSave}>
                Save Changes
              </Button>
              {isDirty && (
                <LinkButton onClick={handleCancel} style={{ border: 'none' }}>
                  Cancel
                </LinkButton>
              )}
            </Actions>
            <EditorPreview>
              <RichTextPage value={templateContent} />
            </EditorPreview>
          </Fragment>
        </ThemeProvider>
      </TemplateEditor>
      <InlineColumn>
        <InstructionsAccepted data={data} style={{ fontSize: '1.3em' }} />
        <TruncatedText style={{ opacity: 0.6 }} textStyle={undefined}>
          {invite
            ? `${
                sortedLastTwoSentAt.filter((lastSent: any) => lastSent !== undefined).length > 1
                  ? 'Last sent at'
                  : 'Sent at'
              } ${format(ZonedDateTime.parse(invite).toLocalDateTime().toString(), 'MM/DD/YYYY HH:mm:ss')}`
            : 'Not Sent'}
        </TruncatedText>
        <ResendActionsInstructions
          data={{ ...data, templateType: selectedTemplate }}
          preSubmit={preSend}
          smsEnabled={smsNotificationData?.sendingSetting !== SendingSetting.Off}
          emailEnabled={emailNotificationData?.sendingSetting !== SendingSetting.Off}
        />
      </InlineColumn>
    </InstructionBody>
  );
};
